<template>
  <div class="task-time-estimate-input">
    <TirPmDatepicker
      v-model="date"
      :type="DatepickerTypeEnum.Time"
      :isDisabled="isDisabled"
      :input="{ isSmall: true, label: 'Время' }"
      @update:modelValue="onTimeUpdate"
    />
    <div class="task-time-estimate-input_buttons">
      <TirPmButton
        :size="TirPmButtonSizeEnum.Small"
        :variant="TirPmButtonVariantEnum.SecondaryState"
        :is-loading="isLoading"
        :is-disabled="!editEstimate"
        @click="saveEstimate"
      >
        Сохранить
      </TirPmButton>
      <TirPmButton
        :size="TirPmButtonSizeEnum.Small"
        :variant="TirPmButtonVariantEnum.LinkState"
        @click="emit('cancel')"
      >
        Отмена
      </TirPmButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  TirPmDatepicker,
  DatepickerTypeEnum,
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
} from 'tir-components';
import { onBeforeMount, PropType, ref, watch } from 'vue';
import { AdapterService } from './adapter-service';
import { PerformerEstimatedModel } from '../../models';

/**Api сервис */
const apiService = new AdapterService();

/**Параметры */
const props = defineProps({
  /**Оценка времени */
  estimate: { type: Number },
  /**Id задачи */
  taskId: { type: Number },
  /**Заблокировать */
  isDisabled: { type: Boolean, default: false },
  /**Может ли текущий пользователь выставлять итоговую оценку времени */
  canUpsertFinalEstimate: { type: Boolean },
  /**Оценка по исполнителю */
  performerEstimate: { type: Object as PropType<PerformerEstimatedModel> },
});
/**События */
const emit = defineEmits<{
  /**Добавление оценки */
  upsert: [time: number];
  /**Отмена добавления оценки */
  cancel: [];
}>();

/**Выбранное время в формате Date */
const date = ref(new Date());
/**Сохранение времени */
const isLoading = ref(false);
/**Оценка на создание */
const editEstimate = ref(0);

/**Отслеживание изменений оценки */
watch(() => props.estimate, setEstimate, { immediate: true });

/**Перед рендером */
onBeforeMount(() => setDate());

function setEstimate() {
  editEstimate.value = props.estimate || 0;
}
/**Инициализация даты */
function setDate() {
  const { hours, minutes } = getHoursAndMinutes(editEstimate.value || 0);

  const newDate = new Date();
  newDate.setHours(Number(hours));
  newDate.setMinutes(Number(minutes));
  newDate.setSeconds(0);
  newDate.setMilliseconds(0);

  date.value = newDate;
}
/**Обновление выбранного времени */
const onTimeUpdate = (date: Date) => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  editEstimate.value = hours + minutes * 0.01;
};
/**Получение часов и минут */
const getHoursAndMinutes = (decimalTime: number) => {
  const [hoursStr, minutesStr = '00'] = decimalTime.toString().split('.');

  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr.padEnd(2, '0').slice(0, 2), 10);

  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(minutes).padStart(2, '0');

  return { hours: `${formattedHours}`, minutes: `${formattedMinutes}` };
};

/**Сохранить оценку */
const saveEstimate = async () => {
  if (!editEstimate.value) return;

  isLoading.value = true;
  const query = props.canUpsertFinalEstimate
    ? apiService.upsertFinalEstimate
    : apiService.upsertEstimate;

  const response = await query.bind(apiService)(
    props.taskId,
    editEstimate.value,
    props.performerEstimate?.Id,
  );
  if (response.IsSuccess) {
    emit('upsert', editEstimate.value);
    editEstimate.value = 0;
    props.performerEstimate.IsEdit = false;
  }
  isLoading.value = false;
};
</script>
<style lang="scss" scoped>
.task-time-estimate-input {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  &_buttons {
    display: flex;
    gap: 0.25rem;
  }
}
</style>
